import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus,
  CheckCircle2,
  Calendar,
  MoreHorizontal,
  Pencil,
  Trash2,
  Loader2,
  Upload,
  FileText,
  MessageSquare,
  ClipboardCheck,
  AlertTriangle,
  Clock,
  CheckSquare,
  Bell,
  Send,
  X
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";

import { API_URL } from "@/utils/api";

// Task type icons and colors
const TASK_TYPE_CONFIG = {
  document_upload: { icon: Upload, color: "bg-blue-100 text-blue-700", label: "Document Upload" },
  form_completion: { icon: FileText, color: "bg-purple-100 text-purple-700", label: "Form Completion" },
  review_acknowledge: { icon: ClipboardCheck, color: "bg-green-100 text-green-700", label: "Review & Acknowledge" },
  information_request: { icon: MessageSquare, color: "bg-amber-100 text-amber-700", label: "Information Request" },
  custom: { icon: CheckSquare, color: "bg-gray-100 text-gray-700", label: "Task" }
};

const PRIORITY_CONFIG = {
  low: { color: "bg-green-100 text-green-700", border: "border-green-300" },
  medium: { color: "bg-yellow-100 text-yellow-700", border: "border-yellow-300" },
  high: { color: "bg-orange-100 text-orange-700", border: "border-orange-300" },
  urgent: { color: "bg-red-100 text-red-700", border: "border-red-300" }
};

export default function TasksPage({ user }) {
  const [tasks, setTasks] = useState([]);
  const [actionTasks, setActionTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Action task modal state
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedActionTask, setSelectedActionTask] = useState(null);
  const [actionResponse, setActionResponse] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    priority: "medium",
    due_date: "",
    category: "General"
  });

  useEffect(() => {
    fetchTasks();
    fetchActionTasks();
  }, []);

  const fetchTasks = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await fetch(`${API_URL}/api/tasks`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setTasks(data);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActionTasks = async () => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      const res = await fetch(`${API_URL}/api/user/action-tasks`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setActionTasks(data);
      }
    } catch (err) {
      console.error("Error fetching action tasks:", err);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem("auth_token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    try {
      const res = await fetch(`${API_URL}/api/tasks`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          ...taskForm,
          completed: false
        })
      });

      if (res.ok) {
        toast.success("Task created!");
        setShowCreateModal(false);
        resetForm();
        fetchTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to create task");
      }
    } catch (err) {
      toast.error("Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    setSaving(true);

    const token = localStorage.getItem("auth_token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    try {
      const res = await fetch(`${API_URL}/api/tasks/${selectedTask.task_id}`, {
        method: "PUT",
        credentials: "include",
        headers,
        body: JSON.stringify(taskForm)
      });

      if (res.ok) {
        toast.success("Task updated!");
        setShowEditModal(false);
        resetForm();
        fetchTasks();
      } else {
        toast.error("Failed to update task");
      }
    } catch (err) {
      toast.error("Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplete = async (task) => {
    const token = localStorage.getItem("auth_token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    // Optimistic update
    setTasks(tasks.map(t =>
      t.task_id === task.task_id ? { ...t, completed: !t.completed } : t
    ));

    try {
      const res = await fetch(`${API_URL}/api/tasks/${task.task_id}`, {
        method: "PUT",
        credentials: "include",
        headers,
        body: JSON.stringify({ ...task, completed: !task.completed })
      });

      if (!res.ok) {
        // Revert on failure
        setTasks(tasks.map(t =>
          t.task_id === task.task_id ? { ...t, completed: task.completed } : t
        ));
        toast.error("Failed to update task");
      }
    } catch (err) {
      // Revert on error
      setTasks(tasks.map(t =>
        t.task_id === task.task_id ? { ...t, completed: task.completed } : t
      ));
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm("Delete this task?")) return;

    const token = localStorage.getItem("auth_token");

    try {
      const res = await fetch(`${API_URL}/api/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (res.ok) {
        toast.success("Task deleted");
        setTasks(tasks.filter(t => t.task_id !== taskId));
      } else {
        toast.error("Failed to delete task");
      }
    } catch (err) {
      toast.error("Failed to delete task");
    }
  };

  // Action task handlers
  const handleOpenActionTask = (task) => {
    setSelectedActionTask(task);
    setActionResponse("");
    setShowActionModal(true);
  };

  const handleCompleteActionTask = async () => {
    if (!selectedActionTask) return;
    setSaving(true);

    const token = localStorage.getItem("auth_token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };

    try {
      const body = {};
      if (selectedActionTask.task_type === "information_request") {
        body.response_text = actionResponse;
      } else if (selectedActionTask.task_type === "review_acknowledge") {
        body.acknowledged = true;
      }

      const res = await fetch(`${API_URL}/api/user/action-tasks/${selectedActionTask.task_id}/complete`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify(body)
      });

      if (res.ok) {
        toast.success("Task completed! Admin has been notified.");
        setShowActionModal(false);
        fetchActionTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to complete task");
      }
    } catch (err) {
      toast.error("Failed to complete task");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedActionTask) return;

    setUploading(true);
    const token = localStorage.getItem("auth_token");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API_URL}/api/user/action-tasks/${selectedActionTask.task_id}/upload`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });

      if (res.ok) {
        toast.success("Document uploaded! Task completed and admin notified.");
        setShowActionModal(false);
        fetchActionTasks();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to upload document");
      }
    } catch (err) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openEditModal = (task) => {
    setSelectedTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || "",
      priority: task.priority || "medium",
      due_date: task.due_date ? task.due_date.split("T")[0] : "",
      category: task.category || "General"
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setTaskForm({
      title: "",
      description: "",
      priority: "medium",
      due_date: "",
      category: "General"
    });
    setSelectedTask(null);
  };

  const filteredTasks = tasks.filter(t => {
    if (filter === "completed") return t.completed;
    if (filter === "pending") return !t.completed;
    return true;
  });

  const pendingActionTasks = actionTasks.filter(t => t.status === "pending");
  const completedActionTasks = actionTasks.filter(t => t.status === "completed");

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.completed).length,
    pending: tasks.filter(t => !t.completed).length,
    overdue: tasks.filter(t => !t.completed && t.due_date && new Date(t.due_date) < new Date()).length,
    actionRequired: pendingActionTasks.length
  };

  const getPriorityColor = (priority) => {
    return PRIORITY_CONFIG[priority]?.color || "bg-gray-100 text-gray-700";
  };

  const TaskFormContent = ({ onSubmit, isEdit }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="title">Task Title *</Label>
        <Input
          id="title"
          value={taskForm.title}
          onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
          placeholder="What needs to be done?"
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={taskForm.description}
          onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Add more details..."
          rows={3}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Priority</Label>
          <Select value={taskForm.priority} onValueChange={(v) => setTaskForm(prev => ({ ...prev, priority: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Category</Label>
          <Select value={taskForm.category} onValueChange={(v) => setTaskForm(prev => ({ ...prev, category: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="General">General</SelectItem>
              <SelectItem value="Finance">Finance</SelectItem>
              <SelectItem value="Tax">Tax</SelectItem>
              <SelectItem value="Bookkeeping">Bookkeeping</SelectItem>
              <SelectItem value="Invoicing">Invoicing</SelectItem>
              <SelectItem value="Admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="due_date">Due Date</Label>
        <Input
          id="due_date"
          type="date"
          value={taskForm.due_date}
          onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
        />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)} className="rounded-full">
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="bg-[#121212] rounded-full">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {isEdit ? "Update Task" : "Create Task"}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#121212] font-['Manrope']">Tasks</h1>
            <p className="text-[#121212]/60 mt-1">Manage your tasks and action items</p>
          </div>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-[#121212] hover:bg-[#2a2a2a] gap-2 rounded-full px-6 font-semibold" data-testid="add-task-btn">
            <Plus className="w-4 h-4" />
            Add Task
          </Button>
        </div>

        {/* Action Required Section */}
        {pendingActionTasks.length > 0 && (
          <Card className="border-2 border-amber-300 bg-amber-50 rounded-2xl" data-testid="action-required-section">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-amber-800 font-['Manrope']">
                <Bell className="w-5 h-5 animate-pulse" />
                Action Required ({pendingActionTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {pendingActionTasks.map((task) => {
                const typeConfig = TASK_TYPE_CONFIG[task.task_type] || TASK_TYPE_CONFIG.custom;
                const TypeIcon = typeConfig.icon;
                const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                
                return (
                  <div 
                    key={task.task_id}
                    className={`bg-white rounded-xl p-4 border-2 ${priorityConfig.border} hover:shadow-md transition-all cursor-pointer`}
                    onClick={() => handleOpenActionTask(task)}
                    data-testid={`action-task-${task.task_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${typeConfig.color}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-bold text-[#121212]">{task.title}</p>
                            <p className="text-sm text-[#121212]/60 mt-0.5 line-clamp-2">{task.description}</p>
                          </div>
                          <Badge className={`rounded-full ${priorityConfig.color} capitalize shrink-0`}>
                            {task.priority}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <Badge variant="outline" className="rounded-full text-xs">
                            {typeConfig.label}
                          </Badge>
                          {task.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${
                              new Date(task.due_date) < new Date() ? 'text-red-500' : 'text-[#121212]/50'
                            }`}>
                              <Clock className="w-3 h-3" />
                              Due {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          )}
                          <span className="text-xs text-[#121212]/40">
                            From {task.created_by_name || "Admin"}
                          </span>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full shrink-0"
                        onClick={(e) => { e.stopPropagation(); handleOpenActionTask(task); }}
                      >
                        {task.action_label || "Take Action"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl hover:border-[#BFFF00] transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">Total Tasks</p>
              <p className="text-2xl font-bold text-[#121212] mt-1 font-['Manrope']">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-green-200 rounded-2xl hover:border-green-400 transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">Completed</p>
              <p className="text-2xl font-bold text-green-600 mt-1 font-['Manrope']">{stats.completed}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-[#BFFF00]/50 rounded-2xl hover:border-[#BFFF00] transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">Pending</p>
              <p className="text-2xl font-bold text-[#121212] mt-1 font-['Manrope']">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-red-200 rounded-2xl hover:border-red-400 transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">Overdue</p>
              <p className="text-2xl font-bold text-red-600 mt-1 font-['Manrope']">{stats.overdue}</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-dashed border-amber-200 rounded-2xl hover:border-amber-400 transition-colors">
            <CardContent className="pt-4">
              <p className="text-sm text-[#121212]/60">Action Required</p>
              <p className="text-2xl font-bold text-amber-600 mt-1 font-['Manrope']">{stats.actionRequired}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {["all", "pending", "completed"].map((f) => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? "bg-[#121212] rounded-full" : "rounded-full border-[#BFFF00]/50 hover:bg-[#BFFF00]/20"}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>

        {/* Tasks List */}
        <Card className="border-2 border-dashed border-[#BFFF00]/30 rounded-2xl">
          <CardContent className="p-0">
            <div className="divide-y divide-[#BFFF00]/10">
              {filteredTasks.length === 0 ? (
                <div className="py-12 text-center">
                  <CheckCircle2 className="w-12 h-12 text-[#121212]/20 mx-auto mb-3" />
                  <p className="font-bold text-[#121212] font-['Manrope']">No tasks found</p>
                  <p className="text-sm text-[#121212]/60 mt-1">
                    {filter === "all" ? "Create your first task to get started." : `No ${filter} tasks.`}
                  </p>
                  {filter === "all" && (
                    <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-[#121212] rounded-full px-6">
                      <Plus className="w-4 h-4 mr-2" /> Add Task
                    </Button>
                  )}
                </div>
              ) : (
                filteredTasks.map((task) => (
                  <div
                    key={task.task_id}
                    className={`flex items-center gap-4 p-4 hover:bg-[#F7FFE5]/50 transition-colors ${task.completed ? 'opacity-60' : ''}`}
                    data-testid={`task-${task.task_id}`}
                  >
                    <Checkbox
                      checked={task.completed}
                      onCheckedChange={() => handleToggleComplete(task)}
                      className="w-5 h-5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${task.completed ? 'line-through text-[#121212]/40' : 'text-[#121212]'}`}>
                        {task.title}
                      </p>
                      {task.description && (
                        <p className="text-sm text-[#121212]/60 truncate">{task.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-[#121212]/50">{task.category}</span>
                        {task.due_date && (
                          <>
                            <span className="text-xs text-[#121212]/30">•</span>
                            <div className={`flex items-center gap-1 text-xs ${
                              !task.completed && new Date(task.due_date) < new Date() ? 'text-red-500' : 'text-[#121212]/50'
                            }`}>
                              <Calendar className="w-3 h-3" />
                              {new Date(task.due_date).toLocaleDateString()}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <Badge className={`rounded-full ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4 text-[#121212]/40" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        <DropdownMenuItem onClick={() => openEditModal(task)}>
                          <Pencil className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteTask(task.task_id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Completed Admin Tasks */}
        {completedActionTasks.length > 0 && (
          <Card className="border-2 border-dashed border-green-200 rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-green-700 font-['Manrope'] text-base">
                <CheckCircle2 className="w-5 h-5" />
                Completed Action Items ({completedActionTasks.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {completedActionTasks.slice(0, 5).map((task) => (
                  <div key={task.task_id} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg opacity-70">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#121212] line-through">{task.title}</p>
                      <p className="text-xs text-[#121212]/50">
                        Completed {task.completed_at ? new Date(task.completed_at).toLocaleDateString() : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Task Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope']">Create New Task</DialogTitle>
          </DialogHeader>
          <TaskFormContent onSubmit={handleCreateTask} isEdit={false} />
        </DialogContent>
      </Dialog>

      {/* Edit Task Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope']">Edit Task</DialogTitle>
          </DialogHeader>
          <TaskFormContent onSubmit={handleUpdateTask} isEdit={true} />
        </DialogContent>
      </Dialog>

      {/* Action Task Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="rounded-2xl border-2 border-[#BFFF00]/30 max-w-lg" data-testid="action-task-modal">
          <DialogHeader>
            <DialogTitle className="font-bold font-['Manrope'] flex items-center gap-2">
              {selectedActionTask && (() => {
                const typeConfig = TASK_TYPE_CONFIG[selectedActionTask.task_type] || TASK_TYPE_CONFIG.custom;
                const TypeIcon = typeConfig.icon;
                return <TypeIcon className="w-5 h-5 text-[#BFFF00]" />;
              })()}
              {selectedActionTask?.title}
            </DialogTitle>
            <DialogDescription className="text-[#121212]/60">
              {selectedActionTask?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedActionTask && (
            <div className="space-y-4 py-4">
              {/* Task Info */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`rounded-full ${PRIORITY_CONFIG[selectedActionTask.priority]?.color || ""}`}>
                  {selectedActionTask.priority}
                </Badge>
                <Badge variant="outline" className="rounded-full">
                  {TASK_TYPE_CONFIG[selectedActionTask.task_type]?.label || "Task"}
                </Badge>
                {selectedActionTask.due_date && (
                  <span className={`text-sm flex items-center gap-1 ${
                    new Date(selectedActionTask.due_date) < new Date() ? 'text-red-500' : 'text-[#121212]/50'
                  }`}>
                    <Clock className="w-4 h-4" />
                    Due {new Date(selectedActionTask.due_date).toLocaleDateString()}
                  </span>
                )}
              </div>

              <div className="text-sm text-[#121212]/60">
                Assigned by <span className="font-medium">{selectedActionTask.created_by_name || "Admin"}</span> on {new Date(selectedActionTask.created_at).toLocaleDateString()}
              </div>

              {/* Action based on task type */}
              {selectedActionTask.task_type === "document_upload" && (
                <div className="space-y-3">
                  <Label className="font-semibold">Upload Document</Label>
                  <div 
                    className="border-2 border-dashed border-[#BFFF00] rounded-xl p-8 text-center cursor-pointer hover:bg-[#F7FFE5] transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? (
                      <Loader2 className="w-8 h-8 animate-spin text-[#121212] mx-auto mb-2" />
                    ) : (
                      <Upload className="w-8 h-8 text-[#121212]/40 mx-auto mb-2" />
                    )}
                    <p className="font-medium text-[#121212]">
                      {uploading ? "Uploading..." : "Click to upload or drag & drop"}
                    </p>
                    <p className="text-xs text-[#121212]/50 mt-1">PDF, Excel, CSV, Images up to 10MB</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                    onChange={handleFileUpload}
                  />
                </div>
              )}

              {selectedActionTask.task_type === "information_request" && (
                <div className="space-y-3">
                  <Label className="font-semibold">Your Response</Label>
                  <Textarea
                    value={actionResponse}
                    onChange={(e) => setActionResponse(e.target.value)}
                    placeholder="Type your response here..."
                    rows={4}
                    className="border-[#BFFF00]/30 focus:ring-[#BFFF00]"
                  />
                </div>
              )}

              {selectedActionTask.task_type === "review_acknowledge" && (
                <div className="bg-[#F7FFE5] p-4 rounded-xl border border-[#BFFF00]/30">
                  <div className="flex items-start gap-3">
                    <ClipboardCheck className="w-6 h-6 text-[#121212] mt-0.5" />
                    <div>
                      <p className="font-medium text-[#121212]">Review & Acknowledge</p>
                      <p className="text-sm text-[#121212]/60 mt-1">
                        By clicking the button below, you acknowledge that you have reviewed and understood the information provided in this task.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedActionTask.task_type === "form_completion" && (
                <div className="bg-[#F7FFE5] p-4 rounded-xl border border-[#BFFF00]/30">
                  <p className="text-sm text-[#121212]/60">
                    Form completion is not yet fully implemented. Please contact support for assistance.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)} className="rounded-full">
              Cancel
            </Button>
            {selectedActionTask?.task_type !== "document_upload" && (
              <Button 
                onClick={handleCompleteActionTask} 
                disabled={saving || (selectedActionTask?.task_type === "information_request" && !actionResponse.trim())}
                className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {selectedActionTask?.action_label || "Submit"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
